<template>
  <div class="order-page">
    <order-page-stats />
    <div class="features f-r-vc">
      <el-button type="primary" class="feature-button deep-green-bg" @click="() => goToCreateOrder()">
        <svg-icon icon-class="plus" class="svg-white-color" style="font-size: 18px;" />
        Create Order
      </el-button>
      <el-button type="primary" class="feature-button deep-green-bg" @click="openDownloadReport">
        <svg-icon icon-class="plus" class="svg-white-color" style="font-size: 18px;" />
        Download Report
      </el-button>
    </div>
    <div class="order-list" v-loading="loadingOrder">
      <el-tabs v-model="queryOrderType">
        <el-tab-pane v-for="(tab, index) in Tabs" :key="'order-tab-' + index" :label="tab" :name="tab" />
      </el-tabs>
      <searchable-table :table-headers="tableHeaders" :table-data="orderPage" :search-params="searchForm"
        :search-param-change="searchParamChange" :has-actions="true">
        <template v-slot:actions="props">
          <el-button size="small" type="text" @click="goToOrderDetail(props.row.no)">
            Detail
          </el-button>
        </template>
        <template #[vcInfo]="props">
          <el-popover placement="right" trigger="hover">
            <basic-form class="global-address-info-basic-form" title="VC Info" :mode="FormLoose"
              :form-params="OrderVCInfoWrapUp(props.row.vcInfo)" />
            <div slot="reference">
              <div class="vc-info-slot">
                <span>PO. {{ props.row.vcInfo?.poNo ?? '' }}</span>
                <svg-icon icon-class="info" class="svg-icon" />
              </div>
            </div>
          </el-popover>
        </template>
        <template #[orderReportCount]="props">
          <div class="scan-report-count">
            <span class="bold-oswald ">
              {{ countReports(props.row.reports) }} / {{ props.row.reports?.length ?? 0 }}
            </span>
            <el-popover placement="right" trigger="hover">
              <div class="shipment-reports">
                <div>Report Records</div>
                <div class="report-record">
                  <span class="shipment-no-title">Shipment No.</span>
                  <span class="reported-at-title">Reported At</span>
                </div>
                <div class="report-record" v-for="(report, index) in (props.row?.reports ?? [])" :key="index">
                  <span class="shipment-no">{{ report.shipmentNo }}</span>
                  <span class="reported-at">{{ report.reportedAt }}</span>
                </div>
              </div>
              <div slot="reference">
                <svg-icon icon-class="info" class="svg-icon" />
              </div>
            </el-popover>
          </div>
        </template>
      </searchable-table>
    </div>
    <confirm ref="downloadReportRef" title="Download Report" :confirmCb="downloadReport">
      <template v-slot:content>
        <el-date-picker size="small" style="width: 100%;" v-model="datePicked" type="date"
          placeholder="Select date and time" />
      </template>
    </confirm>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import set from 'lodash/set'
// mixins
import { pageJumps } from '@/core/mixin/pages'
// apis
// import { queryOrders } from '@/api/order'
// models
import {
  OrderSearchForm,
  OrderTableHeaders,
  OrderSearchParamsWrapUp,
  UnloadingOrder,
  LocalOrder,
  LocalOrderTableHeaders,
  DirectOrder,
  LocalOrderSearchForm,
  OtherOrder,
  UnloadingOrderTableHeaders,
  ScanOrder,
  ScanOrderTableHeaders,
  OrderVCInfoWrapUp
} from '@/core/models/order'
// components
import OrderPageStats from '@/components/order/OrderPageStats.vue'
import SearchableTable from '@/components/common/SearchableTable.vue'
import BasicForm from '@/components/common/BasicForm.vue'
import { Tabs } from '@/core/mixin/tabs'
import { queryOrders } from '@/api/order'
import { FormModes, InitSearchForm } from '@/core/configs/types'
import { getProxyShippingDailyReport } from '@/api/orderProxy'
import { dumpBusinessShippingDailyReport } from '@/core/utils/excel'
import Confirm from '@/components/common/Confirm.vue'

export default {
  name: 'OrderPage',
  mixins: [pageJumps],
  components: {
    SearchableTable,
    OrderPageStats,
    BasicForm,
    Confirm
  },
  data() {
    return {
      FormLoose: FormModes.SINGLE,
      OrderSearchForm,
      loadingOrder: true,
      searchForm: {},
      orderPage: null,
      debounceFetch: debounce(this.doOrderSearch, 300),
      orderReportCount: 'reportCount',
      vcInfo: 'vcInfo',
      datePicked: ''
    }
  },
  watch: {
    queryOrderType: {
      handler: function () {
        switch (this.queryOrderType) {
          case LocalOrder:
            this.$set(
              this,
              'searchForm',
              InitSearchForm(LocalOrderSearchForm, LocalOrderTableHeaders)
            )
            break
          default:
            this.$set(
              this,
              'searchForm',
              InitSearchForm(OrderSearchForm, UnloadingOrderTableHeaders)
            )
        }
        switch (this.queryOrderType) {
          case UnloadingOrder:
          case LocalOrder:
          case DirectOrder:
          case OtherOrder:
          case ScanOrder:
            this.debounceFetch()
            break
          default:
            this.goToSearchOrder({ orderType: UnloadingOrder }, true)
            break
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    queryOrderType: {
      get() {
        return this.$route.query?.orderType
      },
      set(value) {
        this.goToSearchOrder({ orderType: value })
      }
    },
    Tabs() {
      return [UnloadingOrder, LocalOrder, DirectOrder, ScanOrder]
    },
    tableHeaders() {
      switch (this.queryOrderType) {
        case UnloadingOrder:
          return OrderTableHeaders
        case LocalOrder:
          return LocalOrderTableHeaders
        case DirectOrder:
          return OrderTableHeaders
        case ScanOrder:
          return ScanOrderTableHeaders
        default:
          return []
      }
    }
  },
  methods: {
    OrderVCInfoWrapUp,
    searchParamChange(key, path, value) {
      set(this.searchForm[key], path, value)
      this.debounceFetch()
    },
    doOrderSearch() {
      const searchForm = OrderSearchParamsWrapUp(
        this.queryOrderType,
        this.searchForm
      )
      this.loadingOrder = true
      queryOrders(searchForm).then(res => {
        res.records.forEach(one => {
          one.localPalletsCount = (one?.localPallets ?? []).reduce(
            (prev, one) => one.palletCount + prev,
            0
          )
        })
        this.$set(this, 'orderPage', res)
        this.loadingOrder = false
      })
    },
    getOrderDefaultTab(order) {
      return order.type === UnloadingOrder
        ? Tabs.PackingListTab
        : order.type === LocalOrder
          ? Tabs.LocalPalletsGroupTab
          : Tabs.NotesTab
    },
    downloadReport(close, error) {
      if (this.datePicked instanceof Date) {
        const day = String(this.datePicked.getDate()).padStart(2, '0')
        const month = String(this.datePicked.getMonth() + 1).padStart(2, '0')
        const date = `${this.datePicked.getFullYear()}${month}${day}`
        getProxyShippingDailyReport(date).then(res => {
          dumpBusinessShippingDailyReport(res.report, date)
          this.datePicked = ''
          close()
        }).catch(err => {
          error('Download report failed, ' + err)
        })
      } else {
        error('Please select a date')
      }
    },
    openDownloadReport() {
      this.$refs.downloadReportRef.open()
    },
    countReports(reports) {
      return (reports ?? []).filter(one => one.reportedAt != null).length
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.order-page {
  width: 100%;
  overflow-x: hidden;

  @extend .f-c;

  .features {
    @extend .global-margin;
  }

  .order-list {
    @extend .global-margin;
    @extend .shadow-level-three;

    border-radius: $home-card-border-radius;
    padding: $home-card-padding-y $home-card-padding-x;
    background-color: white;
  }
}

.scan-report-count {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 18px;
}

.shipment-reports {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;

  .report-record {
    display: flex;
    flex-direction: row;
    gap: 5px;

    .shipment-no-title {
      font-weight: bold;
    }

    .reported-at-title {
      font-weight: bold;
    }

    .shipment-no-title,
    .shipment-no {
      width: 100px;
    }

    .reported-at-title .reported-at {
      width: 160px;
    }
  }
}

.vc-info-slot {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}
</style>
